import React, { MouseEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import {
  fetchArchive,
  fetchArchivedContact,
  fetchMoreArchive,
  restoreArchivedContact,
  restoreArchivedContactBatch,
  selectArchive,
  selectArchivedContact,
  selectHighlightedArchivedContact,
  selectSelectedArchivedContact,
  selectSelectedArchivedContactBatch,
  setSelectedArchivedContact,
  setSelectedArchivedContactBatch,
} from '@actions/archive';
import Header from '@components/Header';
import { ArchivedContactHeader } from '@components/Contact/Header';
import Container from '@components/ContentContainer';
import ListItem from '@components/ListItem';
import { createListContext, List as VirtualizedList } from '@components/List/VirtualizedList';
import Contact from '@models/Contact';
import styles from './Archive.module.scss';
import ContactAvatar from '@components/ContactAvatar';
import { RestoreIcon } from '@components/Icons';
import KeyboardShortcut, { Key } from '@components/KeyboardShortcut';
import { Overlay } from '@components/Tooltip';
import { RESTORE_HOTKEY } from '@views/CommandBar/actionsBySection/archivedContactActions';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { ID } from '@app/types';
import Sources from '@components/Contact/Sources';
import { ViewID } from '@reducers/ui/viewRestoration';
import { useScrollRestoration } from '@support/hooks/use-scroll-restoration';
import EmptyArchive from './EmptyArchive';
import { PeopleHeader } from '@views/People';
import { GroupMembershipCount } from '@views/Group/Header';
import { metaTrigger } from '@support/keyboardHandler';

const ArchiveListContext = createListContext();

const ArchivedContactAvatar = ({ contact }: { contact: Contact }) => (
  <ListItem.Content className={styles.avatar}>
    <ContactAvatar size="sm" contact={contact} />
  </ListItem.Content>
);

const ArchivedContactActionIcons = ({ contact }) => {
  const dispatch = useDispatch();
  const restoreContact: MouseEventHandler = useCallback((e) => {
    e.stopPropagation();
    dispatch(restoreArchivedContact(contact.id));
  }, []);

  return (
    <ListItem.Content className={styles.rowActions}>
      <KeyboardShortcut
        placement="bottom"
        overlay={
          <Overlay>
            <Key>{RESTORE_HOTKEY}</Key>Restore
          </Overlay>
        }
      >
        <RestoreIcon width={19} height={19} onClick={restoreContact} hoverOpacity />
      </KeyboardShortcut>
    </ListItem.Content>
  );
};

function ArchivedContactRow({ contact, isHighlighted }) {
  return (
    <>
      <ListItem.Content className={cn(styles.contact, isHighlighted && styles.highlighted)}>
        {contact.displayName}
      </ListItem.Content>
      {isHighlighted ? (
        <ArchivedContactActionIcons contact={contact} />
      ) : (
        <ArchivedContactAvatar contact={contact} />
      )}
    </>
  );
}

const renderArchiveItem = (contact: Contact, isHighlighted: boolean) => (
  <ArchivedContactRow contact={contact} isHighlighted={isHighlighted} />
);

const ArchivedContactPreview = ({ contactID }: { contactID?: ID }) => {
  const dispatch = useDispatch();
  const archivedContact = useSelector(selectArchivedContact(contactID));

  useEffect(() => {
    dispatch(fetchArchivedContact(contactID));
  }, [contactID]);

  return (
    !!archivedContact && (
      <div className={styles.sidebarContent}>
        <ArchivedContactHeader className={styles.previewHeader} sidebar contact={archivedContact} />
        <Sources contactID={archivedContact.id} sources={archivedContact?.information} />
      </div>
    )
  );
};

const ArchiveMembershipCount = () => {
  const archive = useSelector(selectArchive);
  const archiveCount = Math.max(archive.persistedItemsOnServer, 0);
  return <GroupMembershipCount count={archiveCount} />;
};

const viewID: ViewID = 'Archive';
export default function Archive() {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const scrollingContainerRef = useRef(null);
  const selectedContactBatch = useSelector(selectSelectedArchivedContactBatch);
  const selectedContact = useSelector(selectSelectedArchivedContact);
  const highlightedContact = useSelector(selectHighlightedArchivedContact);
  const archive = useSelector(selectArchive);
  const archivedContacts = useMemo(() => {
    if (!archive.updated || archive.items.size == 0) {
      return [];
    }

    return [{ id: 'sectionHeader', label: <ArchiveMembershipCount /> }].concat(
      // @ts-ignore
      archive.items.valueSeq().toArray(),
    );
  }, [archive.updated, archive.items]);
  const defaultHighlightedIdx = useMemo(() => {
    if (highlightedContact) {
      const highlightedIdx = archivedContacts.findIndex(({ id }) => id == highlightedContact.id);
      return highlightedIdx || 0;
    }
    return 1;
  }, []);

  const loadNextPage = useCallback(() => dispatch(fetchMoreArchive()), []);

  useScrollRestoration({ viewID, scrollingContainerRef });
  useEffect(() => {
    dispatch(fetchArchive());
  }, []);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.key === RESTORE_HOTKEY && !metaTrigger(e)) {
          e.preventDefault();
          if (selectedContactBatch.size > 0) {
            dispatch(restoreArchivedContactBatch(selectedContactBatch));
          } else if (selectedContact) {
            dispatch(restoreArchivedContact(selectedContact.id));
          }
        }
      },
    },
    [selectedContact, selectedContactBatch],
  );

  return (
    <Container>
      <Container.Primary
        className={styles.container}
        id="content"
        header={
          <Header className={styles.listHeader} actions={false}>
            <div className={styles.listHeaderInner}>
              <PeopleHeader />
              <div ref={headerRef} />
            </div>
          </Header>
        }
      >
        {archive.isLoaded && archive.items.size == 0 && <EmptyArchive />}
        <VirtualizedList
          multiselect
          shouldResetHighlight={false}
          // @ts-ignore
          items={archivedContacts}
          renderItem={renderArchiveItem}
          canLoadMore={archive.canLoadMore}
          loadMore={loadNextPage}
          selectedItems={selectedContactBatch}
          setSelectedItems={(contactIDs) => dispatch(setSelectedArchivedContactBatch(contactIDs))}
          headerRef={headerRef}
          ListContext={ArchiveListContext}
          estimatedCount={archive.persistedItemsOnServer}
          onItemHighlight={(contact: Contact) => dispatch(setSelectedArchivedContact(contact))}
          headerCutoff={1}
          contextMenuTarget="ArchivedContact"
          ref={scrollingContainerRef}
          defaultHighlightedIdx={defaultHighlightedIdx}
          determineItemSize={(item) => (item.id == 'sectionHeader' ? 36 : 48)}
          hasListHeader
        />
      </Container.Primary>
      <Container.Sidebar>
        <ArchivedContactPreview contactID={highlightedContact?.id} />
      </Container.Sidebar>
    </Container>
  );
}
